import { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { mintCoachLiveToken, rememberForLater } from '../../../../apiLayer/processHandlers/coach'
import { useSaveUserGoals, useUpdateUserGoal, useUserGoals } from '../../../../apiLayer/queryHooks/coach'
import { useAssistantRealtimeSession } from '../../../../shared/components/AppAssistant/realtime/useAssistantRealtimeSession'
import type { AriaLiveModality, AssistantLiveTokenResult } from '../../../../../../pure-js/types/AppAssistant'
import {
  GoalSource,
  GoalStatus,
  type UserAiMemoryKind,
  type UserGoal
} from '../../../../../../pure-js/types/CoachTypes'
import { resolveAssistantMessage } from '../../../../shared/components/AppAssistant/appAssistantModel'
import type { ApiResponse } from '../../../../apiLayer/types'
import { createCoachToolDispatcher } from './coachToolDispatcher'

/**
 * Realtime voice for the One-on-One Coach. Reuses the assistant's GeminiLiveTransport + audio pipeline
 * (`useAssistantRealtimeSession`) but mints a COACH ephemeral token (locked coach persona) and carries
 * its own tools. `ensureConversationId` returns/creates the conversation the session is tied to.
 */
/** A stable empty list, so the tool dispatcher is not rebuilt on every render before the goals land. */
const EMPTY_GOALS: UserGoal[] = []

export function useCoachVoiceSession(params: {
  ensureConversationId: () => Promise<string>
  languageCode?: string
  /** Silence timed the session out. The coach saves the transcript before letting it close. */
  onIdleTimeout?: () => void
  /** Overrides what Aria opens with, so a message she typed but never spoke is read aloud. */
  resolveOpeningCue?: () => string
  /** Aria decided the talk is finished. The page hangs up once she has stopped speaking. */
  onConversationComplete?: () => void
  /** Whether this session speaks or writes: a typed one is the same Live session with the audio off, so
   * the context is sent once at setup rather than with every turn. */
  modality?: AriaLiveModality
}) {
  const { ensureConversationId, onIdleTimeout, resolveOpeningCue, onConversationComplete, modality } = params
  const { t, i18n } = useTranslation()
  const conversationIdRef = useRef<string | undefined>(undefined)
  const saveGoalsMutation = useSaveUserGoals()
  const updateGoalMutation = useUpdateUserGoal()
  const goalsQuery = useUserGoals()

  // The app locale, the way the floating assistant does it. Nothing passed one in, so the coach always
  // fell back to the stored user language and the language picker could not change how Aria speaks.
  const languageCode = params.languageCode ?? i18n.language

  const mintToken = useCallback(
    async (modality: AriaLiveModality): Promise<ApiResponse<AssistantLiveTokenResult>> => {
      const conversationId = await ensureConversationId()
      conversationIdRef.current = conversationId
      const res = await mintCoachLiveToken({ conversationId, languageCode, modality })
      return {
        success: res.success,
        data: { config: res.data ?? undefined, unavailable: res.unavailable },
        error: res.error
      }
    },
    [ensureConversationId, languageCode]
  )

  // Goals are saved the moment they are agreed, not when the conversation is concluded — nobody
  // concludes one. The mutation invalidates the caches, so the goals panel fills in mid-conversation.
  // The response carries every goal the person now holds, which is what Aria is told.
  const saveGoals = useCallback(
    async (goals: string[]) => {
      const response = await saveGoalsMutation.mutateAsync({
        conversationId: conversationIdRef.current,
        source: GoalSource.COACH_FLOW,
        goals: goals.map((goal) => ({ goal }))
      })
      return {
        goalCount: response.data.goals.length,
        droppedForLimit: response.data.droppedForLimit,
        // ?? 0 keeps an older backend (no savedCount yet) reading as "wrote something" — the safe side.
        savedCount: response.data.savedCount ?? response.data.goals.length
      }
    },
    [saveGoalsMutation]
  )

  // Retiring a goal frees one of the three slots, so it has to answer with the same shape a save does —
  // Aria needs the new count to know whether the goal she was about to add now fits.
  const completeGoal = useCallback(
    async (goalId: string) => {
      const response = await updateGoalMutation.mutateAsync({ goalId, status: GoalStatus.ACHIEVED })
      const active = response.data.goals.filter((goal) => goal.status === GoalStatus.ACTIVE)
      return { goalCount: active.length, droppedForLimit: 0, savedCount: 0 }
    },
    [updateGoalMutation]
  )

  // Kept the moment they ask, with the talk it was said in, rather than once the talk is closed.
  const rememberNote = useCallback(async (note: string, kind: UserAiMemoryKind) => {
    const response = await rememberForLater({ note, kind, conversationId: conversationIdRef.current })
    return response.success
  }, [])

  // Read through a ref, not a closure: the dispatcher is captured once when the session connects, and
  // Aria saves goals mid-conversation — a snapshot would leave her retiring from a stale list.
  const goalsRef = useRef(goalsQuery.data)
  goalsRef.current = goalsQuery.data
  const getGoals = useCallback(() => goalsRef.current ?? EMPTY_GOALS, [])

  // Held by ref so the dispatcher keeps one identity — rebuilding it mid-session re-registers the tools.
  const onCompleteRef = useRef(onConversationComplete)
  onCompleteRef.current = onConversationComplete
  const endConversation = useCallback(() => onCompleteRef.current?.(), [])

  const dispatchTool = useMemo(
    () =>
      createCoachToolDispatcher({ saveGoals, completeGoal, getGoals, endConversation, rememberForLater: rememberNote }),
    [saveGoals, completeGoal, getGoals, endConversation, rememberNote]
  )

  const session = useAssistantRealtimeSession({
    mintToken,
    modality,
    onIdleTimeout,
    resolveOpeningCue,
    dispatchTool,
    resolveMessage: (message) => resolveAssistantMessage(t, message),
    // The coach's whitelist carries neither tool, so neither runs — they satisfy the shared type.
    navigate: () => undefined,
    getUserStatus: async () => undefined
  })

  return { session, conversationIdRef }
}
